import { useQuery, useQueryClient, useMutation } from 'react-query';
import { TableConfigType } from '../../types/configTypes';
import {
  OutletStatusType,
  CampaignStatusType,
  UpdateStatusType,
  CashbackValueType,
} from '../../types/dataTypes';
import http, { createFetchAPI } from '../http';
import { changeHistoryCacheKey } from '../useFetchChangeHistory';
import { UploadFile } from 'fave-ui';

const endpoint = '/v1/cashback_campaigns';
export const campaignListingCacheKey = 'fetchFLPCashbackListing';
export const campaignDataCachekey = 'fetchFLPCashbackListingData';

export type FLPCashbackListingResponseType = {
  cashback_campaigns: FLPCashbackListingItem[];
  count: number;
};

export type FLPCashbackListingItem = {
  id: number;
  campaign_type: string;
  campaign_name: string;
  funder_type: string;
  funder_id: number;
  funder_name: string;
  currency: string;
  available_budget_amount_cents: number;
  status: OutletStatusType | CampaignStatusType;
  status_event: UpdateStatusType[];
};

export type FLPCashbackDataItem = {
  id: number | string;
  campaign_type: string;
  campaign_name: string;
  funder_type: string;
  funder_id: number;
  funder_name: string;
  currency: string;
  min_transaction_amount_cents?: number;
  min_monthly_transaction_count?: number;
  cashback_value_type: CashbackValueType;
  cashback_rate?: number;
  cashback_amount_cents?: number;
  max_cashback_amount_cents?: number;
  max_budget_amount_cents: number;
  available_budget_amount_cents: number;
  status: OutletStatusType | CampaignStatusType;
  whitelisted_outlet_ids?: number[];
  start_date?: string;
  end_date?: string;
  description?: string;
  image_url?: string;
};

export type FLPCashbackParamsType = TableConfigType & {
  funder_type: string;
};

export type FunderType = {
  campaign_type: string;
  value: 'Flp::Client' | 'Fave' | 'BKE::ECard';
};

type FLPPartner = {
  funder_id: number;
  funder_type: string;
  funder_name: string;
};

export type CashbackCampaignOptions = {
  funder_types: FunderType[];
  currency: string;
};

export type FLPPartnerOptions = {
  flp_partners: FLPPartner[];
  cashback_value_types: CashbackValueType[];
};

export type UpdateFLPCashbackStatus = {
  status_event: UpdateStatusType;
  id: number | string;
};

export type UpdateFLPCashbackWhitelisted = {
  whitelisted_outlet_ids?: (string | number)[];
  id: number | string;
};

export type OptionsParamsType = {
  funder_type?: FunderType['value'];
};

export type AddCashbackCampaignPayload = {
  campaign_type?: string;
  campaign_name: string;
  funder_type?: string;
  funder_id?: number;
  funder_name?: string;
  min_transaction_amount_cents?: number;
  min_monthly_transaction_count?: number;
  cashback_value_type?: CashbackValueType;
  cashback_rate?: number;
  cashback_amount_cents?: number;
  max_cashback_amount_cents?: number;
  max_budget_amount_cents?: number;
  whitelisted_outlet_ids?: string[] | string;
  start_date?: string;
  end_date?: string;
  description?: string;
  image?: UploadFile[];
};

const fetchFLPCashbackListing = createFetchAPI<
  FLPCashbackParamsType,
  FLPCashbackListingResponseType
>(endpoint);

export const useFetchCashbackCampaignListing = (props: FLPCashbackParamsType) =>
  useQuery<FLPCashbackListingResponseType>(
    [campaignListingCacheKey, { ...props }],
    fetchFLPCashbackListing,
    { staleTime: 300_000 },
  );

const fetchCashbackCampaignListingData = createFetchAPI<
  TableConfigType,
  FLPCashbackDataItem
>();

export const useFetchCashbackCampaignListingData = ({
  id,
}: {
  id?: number | string;
}) =>
  useQuery<FLPCashbackDataItem>(
    [campaignDataCachekey, { url: `${endpoint}/${id}` }],
    fetchCashbackCampaignListingData,
    { enabled: !!id },
  );

const fetchCashbackCampaignOptions = createFetchAPI<
  OptionsParamsType,
  CashbackCampaignOptions
>(`${endpoint}/options`);

export const useFetchCashbackCampaignOptions = (props: OptionsParamsType) =>
  useQuery<CashbackCampaignOptions>(
    ['fetchCashbackCampaignOptions', { ...props }],
    fetchCashbackCampaignOptions,
    { staleTime: 300_000 },
  );

const fetchCashbackPartnerOptions = createFetchAPI<
  OptionsParamsType,
  FLPPartnerOptions
>(`${endpoint}/options`);

export const useFetchCashbackPartnerOptions = (props: OptionsParamsType) =>
  useQuery<FLPPartnerOptions>(
    ['fetchCashbackPartnerOptions', { ...props }],
    fetchCashbackPartnerOptions,
    { staleTime: 300_000 },
  );

export const addCashbackCampaign = (payload: AddCashbackCampaignPayload) =>
  http.post<AddCashbackCampaignPayload>(endpoint, payload);

export function useAddCashbackCampaignMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    async (payload: AddCashbackCampaignPayload) => addCashbackCampaign(payload),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(campaignListingCacheKey);
      },
    },
  );
}

type MutateAddECardCampaignPayload = { formData: FormData };

const addEcardCampaignMutation = (formData: FormData) =>
  http.post<MutateAddECardCampaignPayload>(endpoint, formData);

export const useAddEcardCampaignMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (payload: MutateAddECardCampaignPayload) =>
      addEcardCampaignMutation(payload.formData),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(campaignListingCacheKey);
        queryClient.invalidateQueries(changeHistoryCacheKey, {
          refetchInactive: true,
        });
      },
    },
  );
};

type MutateEditEcardCampaignPayload = {
  id?: string;
  formData: FormData;
};

const editEcardCampaignMutation = ({
  id,
  formData,
}: MutateEditEcardCampaignPayload) =>
  http.put<MutateEditEcardCampaignPayload>(`${endpoint}/${id}`, formData);

export const useEditEcardCampaignMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (payload: MutateEditEcardCampaignPayload) =>
      editEcardCampaignMutation(payload),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(campaignListingCacheKey);
        queryClient.invalidateQueries(campaignDataCachekey);
        queryClient.invalidateQueries(changeHistoryCacheKey, {
          refetchInactive: true,
        });
      },
    },
  );
};

export const updateFLPCashbackStatus = (payload: UpdateFLPCashbackStatus) =>
  http.put<UpdateFLPCashbackStatus>(
    `${endpoint}/${payload.id}/update_status`,
    payload,
  );

export function useUpdateCashbackCampaignMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    async (payload: UpdateFLPCashbackStatus) =>
      updateFLPCashbackStatus(payload),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(campaignListingCacheKey);
        queryClient.invalidateQueries(campaignDataCachekey);
        queryClient.invalidateQueries(changeHistoryCacheKey, {
          refetchInactive: true,
        });
      },
    },
  );
}

const updateFLPCashbackWhitelisted = (payload: UpdateFLPCashbackWhitelisted) =>
  http.put<UpdateFLPCashbackWhitelisted>(`${endpoint}/${payload.id}`, payload);

export const useUpdateFLPWhitelistedMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload: UpdateFLPCashbackWhitelisted) =>
      updateFLPCashbackWhitelisted(payload),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(campaignListingCacheKey);
        queryClient.invalidateQueries(campaignDataCachekey);
        queryClient.invalidateQueries(changeHistoryCacheKey, {
          refetchInactive: true,
        });
      },
    },
  );
};
